import { format } from 'date-fns';
import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import type Link from 'react-csv/components/Link';

import type { LineItemMetrics } from '../../../../../../../apis/graphql';
import { getReportPreviewColumns } from '../ReportPreviewTable/getReportPreviewColumns';

export interface CSVLinkRef extends HTMLAnchorElement, Link {
  link: HTMLAnchorElement;
}

type UseReportExportResponse = {
  dataToExport: string[][];
  csvFullExportLink: RefObject<CSVLinkRef>;
  onClickExport: () => void;
  exportFileName: string;
};

type Props = {
  windowsCount: number;
  lineItemList: LineItemMetrics[];
};

const useReportExport = ({ windowsCount, lineItemList }: Props): UseReportExportResponse => {
  const csvFullExportLink = useRef<CSVLinkRef>(null);
  const [isStartedExporting, setIsStartedExporting] = useState<boolean>(false);
  const [dataToExport, setDataToExport] = useState<string[][]>([]);

  useEffect(() => {
    if (isStartedExporting && dataToExport.length > 1) {
      setIsStartedExporting(false);
      setTimeout(() => {
        csvFullExportLink.current?.link.click();
      });
    }
  }, [isStartedExporting, dataToExport]);

  const onClickExport = (): void => {
    if (!lineItemList.length) return;

    setIsStartedExporting(true);
    const columnsObject = getReportPreviewColumns(windowsCount);

    const headers: string[] = columnsObject.map((column) => column.Header);
    const rowsToExport: string[][] = lineItemList.map((lineItem) => {
      return columnsObject.map(({ accessor }) => accessor(lineItem).toString());
    });

    setDataToExport([headers, ...rowsToExport]);
  };

  const exportFileName = `custom_pacing_report_preview_${format(new Date(), "MMddyy'_'hhmma")}.csv`;

  return {
    dataToExport,
    csvFullExportLink,
    onClickExport,
    exportFileName,
  };
};

export default useReportExport;
